/**
 * Shared copy + formatting helpers for the pending-plan-change UI
 * (feature 018-native-plan-changes, FR-018).
 *
 * Used by `SubscriptionSummary.vue` (T086) to render the scheduled-change
 * callout — change the wording here, not in the component.
 */

import { namedAmount } from 'app/views/accounts/addOns/addOnResources'

const PLAN_NAME_BY_SKU = {
  'FA-STARTER': 'Starter',
  'FA-STARTER-MONTHLY': 'Starter Monthly',
  'FA-SOLO': 'Solo',
  'FA-SOLO-MONTHLY': 'Solo Monthly',
  'FA-TEAM': 'Team',
  'FA-TEAM-MONTHLY': 'Team Monthly'
}

export function pendingChangePlanName(targetPlan) {
  if (!targetPlan) return ''
  return PLAN_NAME_BY_SKU[targetPlan] || targetPlan
}

export function formatPendingChangeDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

// The notice names the plan and the date, and stops there.
//
// It used to quote what the new plan renews at, from `renewalAmountCents` — the
// plan's own price plus seats, and nothing else. That figure disagreed with the
// table below it for anyone carrying an add-on or a discount, and two prices in
// one card is one too many. The Upcoming Plan tab's renewal total is where that
// question is answered, itemized and reconciled against Stripe.
export function pendingChangeMessage(pendingChange) {
  if (!pendingChange) return ''
  const planName = pendingChangePlanName(pendingChange.targetPlan)
  const date = formatPendingChangeDate(pendingChange.effectiveAt)
  return `Your plan changes to the ${planName} plan on ${date}.`
}

function unitsList(addOns) {
  return joinList(addOns.map((addOn) => namedAmount(addOn.resource, addOn.effectiveUnits)))
}

function joinList(parts) {
  if (parts.length <= 1) return parts.join('')
  if (parts.length === 2) return parts.join(' and ')
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`
}

/**
 * What the customer's capacity does at the boundary, for the callout that
 * offers to cancel the change (FR-030–FR-032).
 *
 * Two things can be true at once and they pull opposite ways, so both are said:
 * a plan change replaces the add-on set, so packs held on the plan being left
 * end with it, while packs bought as part of the change start with it. Naming
 * only one half would make canceling look free when it isn't, or costly when
 * it isn't.
 *
 * A pack ending here is only mentioned when the CHANGE is what took it —
 * `supersededByPlanChange`. One the customer removed themselves ends on the
 * same date in the same state, but canceling the change won't bring it back,
 * so promising otherwise would be a lie.
 */
export function pendingChangeAddOnMessage(addOns) {
  const held = Array.isArray(addOns) ? addOns : []
  const starting = held.filter((addOn) => addOn.state === 'scheduled')
  const ending = held.filter((addOn) => addOn.state === 'scheduled_for_removal' && addOn.supersededByPlanChange)

  const sentences = []

  if (ending.length > 0) {
    sentences.push(`The ${unitsList(ending)} you added to your current plan end with it — cancel the change and you keep them.`)
  }

  if (starting.length > 0) {
    sentences.push(
      `The ${unitsList(starting)} you bought start with the new plan. Nothing has been charged for them yet, and canceling the change cancels them too.`
    )
  }

  return sentences.join(' ')
}
